import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  documentsApi,
  DOC_CATEGORIES,
  type DocCategory,
  type ProviderDoc,
} from '../api/documents';
import { EmptyState, Loading, PageError } from '../components/State';
import { Modal } from '../components/Modal';
import { isApiError } from '../lib/apiClient';
import { openAttachment, printAttachment } from '../lib/attachment';
import { fileToBase64, MAX_FILE_BYTES } from '../lib/fileToBase64';
import { fmtDate, todayIso } from '../lib/format';
import './InvoicesPage.css';
import './MileagePage.css';
import './DocumentsPage.css';

const CATEGORY_LABEL: Record<DocCategory, string> = {
  certifications: 'Certifications',
  training: 'Training',
  agreements: 'Agreements',
  insurance: 'Insurance',
  reports: 'Reports',
  other: 'Other',
};

function expiryTone(iso: string | null): 'ok' | 'soon' | 'past' | null {
  if (!iso) return null;
  const days = Math.ceil((new Date(`${iso}T00:00:00`).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return 'past';
  if (days <= 30) return 'soon';
  return 'ok';
}

export function DocumentsPage() {
  const [showAdd, setShowAdd] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['documents'],
    queryFn: documentsApi.list,
  });

  const del = useMutation({
    mutationFn: (id: string) => documentsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });

  if (isLoading) return <Loading />;

  const byCategory = new Map<DocCategory, ProviderDoc[]>();
  for (const d of data ?? []) {
    const arr = byCategory.get(d.category) ?? [];
    arr.push(d);
    byCategory.set(d.category, arr);
  }

  return (
    <div>
      <div className="page-head">
        <h1>Documents</h1>
        <button className="btn" onClick={() => setShowAdd(true)}>
          Add document
        </button>
      </div>

      {isError ? (
        <PageError error={error} onRetry={() => refetch()} />
      ) : (data ?? []).length === 0 ? (
        <EmptyState text="No documents yet. Keep certifications, insurance and agreements here." />
      ) : (
        DOC_CATEGORIES.filter((c) => byCategory.has(c)).map((cat) => (
          <section key={cat} className="doc__group">
            <h2>{CATEGORY_LABEL[cat]}</h2>
            <div className="card inv__list">
              {byCategory.get(cat)!.map((d) => {
                const tone = expiryTone(d.expiryDate);
                return (
                  <div key={d.id} className="doc__row">
                    <div className="doc__row-main">
                      <div className="doc__row-title">{d.title}</div>
                      <div className="muted doc__row-sub">
                        {d.fileName ?? 'No file'}
                        {d.expiryDate && (
                          <span className={`doc__expiry doc__expiry--${tone}`}>
                            {tone === 'past' ? 'Expired ' : 'Expires '}
                            {fmtDate(d.expiryDate)}
                          </span>
                        )}
                      </div>
                      {d.notes && <div className="muted doc__row-sub">{d.notes}</div>}
                    </div>
                    {d.hasFile && <DownloadButton id={d.id} />}
                    {d.hasFile && <PrintButton id={d.id} />}
                    <button
                      className="mil__del"
                      disabled={del.isPending}
                      onClick={() => {
                        if (confirm(`Delete "${d.title}"?`)) del.mutate(d.id);
                      }}
                      aria-label="Delete"
                    >
                      🗑
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}

      {showAdd && <AddDocModal onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function DownloadButton({ id }: { id: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      className="btn btn--ghost btn--sm"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const f = await documentsApi.getFile(id);
          openAttachment({ fileData: f.fileData, fileName: f.fileName, mimeType: f.mimeType });
        } catch {
          alert('Could not open the file.');
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? '…' : 'Open'}
    </button>
  );
}

function PrintButton({ id }: { id: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      className="btn btn--ghost btn--sm"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const f = await documentsApi.getFile(id);
          printAttachment({ fileData: f.fileData, fileName: f.fileName, mimeType: f.mimeType });
        } catch {
          alert('Could not print the file.');
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? '…' : 'Print'}
    </button>
  );
}

function AddDocModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<DocCategory>('other');
  const [notes, setNotes] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [localErr, setLocalErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const doc = await documentsApi.create({
        title: title.trim(),
        category,
        notes: notes.trim(),
        expiryDate: expiryDate || undefined,
      });
      if (file) {
        const fileData = await fileToBase64(file);
        await documentsApi.uploadFile(doc.id, {
          fileData,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          fileSize: file.size,
        });
      }
      return doc;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      onClose();
    },
  });

  return (
    <Modal
      title="Add document"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn"
            disabled={!title.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="field">
        <label>Title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. First Aid certificate" />
      </div>
      <div className="inv__two">
        <div className="field">
          <label>Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value as DocCategory)}>
            {DOC_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Expiry date (optional)</label>
          <input type="date" min={todayIso()} value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label>Notes</label>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
      </div>
      <div className="field">
        <label>File (optional, max 10 MB)</label>
        <input
          type="file"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            if (f && f.size > MAX_FILE_BYTES) {
              setLocalErr('That file is larger than 10 MB.');
              setFile(null);
            } else {
              setLocalErr(null);
              setFile(f);
            }
          }}
        />
      </div>

      {localErr && <p className="error-text">{localErr}</p>}
      {save.isError && (
        <p className="error-text">
          {isApiError(save.error) ? save.error.message : 'Could not save the document.'}
        </p>
      )}
    </Modal>
  );
}
